import { assert } from '@fe/core';
import { MapMouseEvent, Subscription } from 'maplibre-gl';
import { GDFPalletCode } from '../../gdf/type';
import { WeatherPickingInfoLayer } from '../../manager/types';
import { getPalette } from '../../palettes/palettes';
import products from '../../products/products';
import { BarbColorType, WeatherSettingsChangeSource } from '../../settings/settings';
import { generateRampFromPalette } from '../../utils/ramp';
import { MessageType } from '../../worker/actor/actor-messages';
import { CircleLayer } from '../custom/circle/circle-layer';
import { SymbolLayer } from '../custom/symbol/symbol-layer';
import { barbSizeToMultiplier } from '../geojson/styles/common';
import { WeatherLayerBase } from '../weather-layer-base';

const radiusExpression = (zoom: number) => {
    if (zoom < 2) {
        return 1.5;
    }
    if (zoom < 4) {
        return 3;
    }
    if (zoom < 6) {
        return 4;
    }
    if (zoom < 8) {
        return 5;
    }
    return 6;
};

export class IataTurbulenceLayer extends WeatherLayerBase {
    private circleLayer: CircleLayer | null = null;
    private barbsLayer: SymbolLayer | null = null;
    private themeSub: Subscription | null = null;

    async pickObjects(event: MapMouseEvent): Promise<WeatherPickingInfoLayer | WeatherPickingInfoLayer[] | null> {
        const radiusMeters =
            radiusExpression(this.map.getZoom()) /
            (this.map._camera.transform.pixelsPerMeter ?? (this.map._camera.transform as any)._mercatorTransform.pixelsPerMeter);

        const report = await this.dispatcher.dispatch({
            type: MessageType.getHoverDataIataTurb,
            data: {
                id: this.productId,
                frame: this.visibleFrame!,
                position: event.lngLat.toArray(),
                radiusMeters: radiusMeters,
                workspaceId: this.workspaceId
            }
        });

        if (!report) {
            return null;
        }

        return {
            product: products[this.productId],
            palette: GDFPalletCode.iataturb,
            objects: [
                {
                    type: 'record',
                    record: report.record,
                    id: report.id
                }
            ]
        };
    }

    protected onAdd(): void {
        assert(!this.circleLayer);
        this.circleLayer = new CircleLayer(this.baseId + '-points');
        this.barbsLayer = new SymbolLayer(this.baseId + '-barbs');
        this.circleLayer.radius = radiusExpression;
        this.barbsLayer.size = barbSizeToMultiplier(this.settings.winds.barbSize) * 40;
        this.barbsLayer.setBarbsColor(this.getColor());
        this.barbsLayer.setBarbsColorType(this.settings.winds.barbColorType);
        this.map.addLayer(this.barbsLayer, this.slotTopLayerId);
        this.map.addLayer(this.circleLayer, this.slotTopLayerId);
        this.themeSub = this.map.onThemeChanged(() => {
            if (this.barbsLayer) {
                this.barbsLayer.setBarbsColor(this.getColor());
            }
        });
    }

    protected onRemove(): void {
        if (this.circleLayer) {
            this.map.removeLayer(this.circleLayer.id);
        }
        if (this.barbsLayer) {
            this.map.removeLayer(this.barbsLayer.id);
        }
        this.themeSub?.unsubscribe();
    }

    protected onDisplayChanged(): void {
        if (this.circleLayer) {
            this.map.setLayoutProperty(this.circleLayer.id, 'visibility', this.shouldDisplay() ? 'visible' : 'none');
        }

        if (this.barbsLayer) {
            this.map.setLayoutProperty(
                this.barbsLayer.id,
                'visibility',
                this.shouldDisplay() && this.settings.turbulenceIcing.iataShowWindBarbs ? 'visible' : 'none'
            );
        }
    }

    protected async loadFrame(frameId: number) {
        const palette = getPalette(this.info!.frames[0].header.productPallet, this.settings);
        if (palette) {
            const ramp = generateRampFromPalette(palette);
            this.circleLayer?.setRamp(ramp);
        }

        const response = await this.dispatcher.dispatch({
            type: MessageType.getDisplayDataIataTurb,
            data: {
                id: this.productId,
                frame: frameId,
                workspaceId: this.workspaceId
            }
        });
        this.circleLayer?.setData(response.items);
        this.barbsLayer?.setData(response.items, (x) => getWindImageName(x.speed), getPalette(GDFPalletCode.wind_heatmap, this.settings)!);
    }

    protected async onSettingsUpdated(changeSource: WeatherSettingsChangeSource) {
        if (changeSource == WeatherSettingsChangeSource.turbulenceIata) {
            if (this.barbsLayer) {
                this.map.setLayoutProperty(
                    this.barbsLayer.id,
                    'visibility',
                    this.shouldDisplay() && this.settings.turbulenceIcing.iataShowWindBarbs ? 'visible' : 'none'
                );
            }
            await this.loadProduct();
            if (this.visibleFrame !== null) {
                await this.loadFrame(this.visibleFrame);
            }
        } else if (changeSource === WeatherSettingsChangeSource.windsBarbSize) {
            if (this.barbsLayer) {
                this.barbsLayer.size = barbSizeToMultiplier(this.settings.winds.barbSize) * 40;
            }
        } else if (changeSource === WeatherSettingsChangeSource.windsBarbsColor) {
            if (this.barbsLayer) {
                this.barbsLayer.setBarbsColorType(this.settings.winds.barbColorType);
                this.barbsLayer.setBarbsColor(this.getColor());
            }
        }
    }

    private getColor() {
        return this.settings.winds.barbColorType === BarbColorType.auto
            ? (this.map.getGlobalState()['text-color'] ?? '#000')
            : this.settings.winds.barbColorType === BarbColorType.fixed
              ? this.settings.winds.barbColor
              : null;
    }
}

function getWindImageName(input: number | null): string | null {
    if (input === null || input === 0.0) {
        return null;
    }
    // TODO: improve this mapping lol
    /* eslint-disable curly */
    if (input <= 0) return 'fe-weather:wind-0';
    if (input <= 7) return 'fe-weather:wind-7';
    if (input <= 12) return 'fe-weather:wind-12';
    if (input <= 17) return 'fe-weather:wind-17';
    if (input <= 22) return 'fe-weather:wind-22';
    if (input <= 27) return 'fe-weather:wind-27';
    if (input <= 32) return 'fe-weather:wind-32';
    if (input <= 37) return 'fe-weather:wind-37';
    if (input <= 42) return 'fe-weather:wind-42';
    if (input <= 47) return 'fe-weather:wind-47';
    if (input <= 52) return 'fe-weather:wind-52';
    if (input <= 57) return 'fe-weather:wind-57';
    if (input <= 62) return 'fe-weather:wind-62';
    if (input <= 67) return 'fe-weather:wind-67';
    if (input <= 72) return 'fe-weather:wind-72';
    if (input <= 77) return 'fe-weather:wind-77';
    if (input <= 82) return 'fe-weather:wind-82';
    if (input <= 87) return 'fe-weather:wind-87';
    if (input <= 92) return 'fe-weather:wind-92';
    if (input <= 97) return 'fe-weather:wind-97';
    if (input <= 102) return 'fe-weather:wind-102';
    if (input <= 107) return 'fe-weather:wind-107';
    if (input <= 112) return 'fe-weather:wind-112';
    if (input <= 117) return 'fe-weather:wind-117';
    if (input <= 122) return 'fe-weather:wind-122';
    if (input <= 127) return 'fe-weather:wind-127';
    if (input <= 132) return 'fe-weather:wind-132';
    if (input <= 137) return 'fe-weather:wind-137';
    if (input <= 142) return 'fe-weather:wind-142';
    if (input <= 147) return 'fe-weather:wind-147';
    if (input <= 152) return 'fe-weather:wind-152';
    if (input <= 202) return 'fe-weather:wind-202';
    return null;
}
